import { EventItem } from "@/src/models/event.model";
import { format } from "date-fns";
import { Location } from "iconsax-react-nativejs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, Text, View } from "react-native";

type EventsNextUpProps = Readonly<{
  events: EventItem[];
  onSeeAll: () => void;
  onEventPress: (event: EventItem) => void;
}>;

type NextUpItemProps = Readonly<{
  event: EventItem;
  colorIndex: number;
  onPress: () => void;
}>;

const MAX_NEXT_UP_EVENTS = 4;

const BADGE_COLORS: readonly Readonly<{ bg: string; text: string }>[] = [
  { bg: "bg-blue-100", text: "text-blue-700" },
  { bg: "bg-purple-100", text: "text-purple-700" },
  { bg: "bg-green-100", text: "text-green-700" },
  { bg: "bg-orange-100", text: "text-orange-700" },
];

const NextUpItem = ({ event, colorIndex, onPress }: NextUpItemProps) => {
  const badgeColor = BADGE_COLORS[colorIndex % BADGE_COLORS.length];
  const startDate = new Date(event?.startTime);
  const endDate = new Date(event?.endTime);
  const month = format(startDate, "MMM").toUpperCase();
  const day = format(startDate, "dd");
  const startFormatted = format(startDate, "h:mm a");
  const endFormatted = format(endDate, "h:mm a");
  const eventLocation = event?.locationName ?? null;

  return (
    <Pressable
      onPress={onPress}
      className="flex-row items-start gap-3 p-3 rounded-2xl border border-ash-3 bg-chalk hover:bg-ash-3"
    >
      <View
        className={`w-12 h-14 shrink-0 rounded-xl items-center justify-center ${badgeColor.bg}`}
      >
        <Text className={`text-xs font-semibold ${badgeColor.text}`}>
          {month}
        </Text>
        <Text className={`text-lg font-bold ${badgeColor.text}`}>{day}</Text>
      </View>
      <View className="flex-1 gap-0.5 min-w-0">
        <Text className="text-sm font-semibold text-onyx" numberOfLines={1}>
          {event?.title}
        </Text>
        <Text className="text-xs text-onyx" numberOfLines={1}>
          {startFormatted} - {endFormatted}
        </Text>
        {eventLocation && (
          <View className="flex-row items-center gap-1">
            <Location size={14} color={"red"} />
            <Text className="text-xs flex-1 text-onyx" numberOfLines={1}>
              {eventLocation}
            </Text>
          </View>
        )}
      </View>
    </Pressable>
  );
};

const EventsNextUp = ({
  events,
  onSeeAll,
  onEventPress,
}: EventsNextUpProps) => {
  const { t } = useTranslation();

  const nextUpEvents = useMemo(() => {
    const now = new Date();
    return [...events]
      .filter((event) => new Date(event?.startTime) > now)
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      )
      .slice(0, MAX_NEXT_UP_EVENTS);
  }, [events]);

  if (nextUpEvents.length === 0) return null;

  return (
    <View className="rounded-2xl border border-ash-3 bg-chalk p-4 gap-3">
      <View className="flex-row items-center justify-between">
        <Text className="text-sm font-bold text-onyx">
          {t("LABEL_NEXT_UP")}
        </Text>
        <Pressable
          onPress={onSeeAll}
          className="px-2 py-1 hover:bg-ash rounded"
        >
          <Text className="text-xs text-primary">{t("LABEL_SEE_ALL")}</Text>
        </Pressable>
      </View>
      <View className="gap-2">
        {nextUpEvents.map((event, index) => (
          <NextUpItem
            key={event.ItemId}
            event={event}
            colorIndex={index}
            onPress={() => onEventPress(event)}
          />
        ))}
      </View>
    </View>
  );
};

export default EventsNextUp;
